/**
 * Notices: what the committee wants the building to know.
 *
 * The phone and the desktop both post them and both read them, so the shape is
 * written once here. They had drifted: the desktop wrote `createdAt` and no
 * urgency, the phone wrote `postedAt`, and each showed the other's notices
 * with no date. A notice written either way still reads.
 */

export const NOTICE_TITLE_MAX = 120;
export const NOTICE_BODY_MAX = 2000;
/** The longest a notice may be set to stay up, in days. */
export const NOTICE_DAYS_MAX = 90;

const DAY_MS = 86_400_000;

export interface Announcement {
  id: string;
  title: string;
  body: string;
  /** Urgent notices sort to the top, are marked, and are pushed to residents' phones. */
  urgent: boolean;
  postedAt: Date | null;
  expiresAt: Date | null;
}

export class NoticeError extends Error {}

function dateOf(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

export function toAnnouncement(id: string, data: Record<string, unknown>): Announcement {
  return {
    id,
    title: typeof data.title === "string" ? data.title : "",
    body: typeof data.body === "string" ? data.body : "",
    urgent: data.urgent === true,
    postedAt: dateOf(data.postedAt) ?? dateOf(data.createdAt),
    expiresAt: dateOf(data.expiresAt),
  };
}

/** What a committee member typed, checked and shaped the way the rules accept. */
export function announcementInput(
  input: { title: string; body: string; urgent: boolean; days?: number | null },
  now: Date = new Date(),
): { title: string; body: string; urgent: boolean; expiresAt: Date | null } {
  const title = input.title.trim();
  const body = input.body.trim();
  if (title === "" || title.length > NOTICE_TITLE_MAX) {
    throw new NoticeError(
      `A notice needs a title of at most ${String(NOTICE_TITLE_MAX)} characters.`,
    );
  }
  if (body === "" || body.length > NOTICE_BODY_MAX) {
    throw new NoticeError(
      `A notice needs a message of at most ${String(NOTICE_BODY_MAX)} characters.`,
    );
  }
  const days = input.days ?? null;
  if (days !== null && (!Number.isInteger(days) || days < 1 || days > NOTICE_DAYS_MAX)) {
    throw new NoticeError(`A notice stays up for 1 to ${String(NOTICE_DAYS_MAX)} days.`);
  }
  return {
    title,
    body,
    urgent: input.urgent,
    expiresAt: days === null ? null : new Date(now.getTime() + days * DAY_MS),
  };
}

/** What is still up, urgent first, then newest. */
export function currentNotices(
  all: readonly Announcement[],
  now: Date = new Date(),
): Announcement[] {
  return all
    .filter(
      (notice) =>
        notice.title !== "" &&
        (notice.expiresAt === null || notice.expiresAt.getTime() > now.getTime()),
    )
    .sort(
      (a, b) =>
        Number(b.urgent) - Number(a.urgent) ||
        (b.postedAt?.getTime() ?? 0) - (a.postedAt?.getTime() ?? 0),
    );
}
